import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CHART, OpsChart } from "../components/OpsChart";
import { opsFetch } from "../lib/api";

type Board = {
  data: {
    days: number;
    pv: number;
    uv: number;
    clicks: number;
    click_uv: number;
    estimated_cny: number;
    conversion: number;
    series: Array<{ date: string; pv: number; uv: number; clicks: number }>;
    rows: Array<{
      key: string;
      merchant_slug: string | null;
      kind: string | null;
      clicks: number;
      click_uv: number;
      cpa_cny: number;
      estimated_cny: number;
    }>;
  };
};

export function SettlementPage() {
  const [days, setDays] = useState(30);
  const q = useQuery({
    queryKey: ["ops-settlement", days],
    queryFn: () => opsFetch<Board>(`/api/ops/settlement?days=${days}`),
  });
  const data = q.data?.data;
  const money = useMemo(() => {
    const ranked = [...(data?.rows ?? [])].sort((a, b) => (data?.estimated_cny ? b.estimated_cny - a.estimated_cny : b.clicks - a.clicks));
    const rows = ranked.slice(0, 8).reverse();
    const byMoney = (data?.estimated_cny ?? 0) > 0;
    return {
      tooltip: { trigger: "axis" as const },
      grid: { left: 110, right: 16, top: 12, bottom: 24 },
      xAxis: { type: "value" as const, axisLabel: { color: CHART.muted } },
      yAxis: { type: "category" as const, data: rows.map((row) => row.key), axisLabel: { color: CHART.muted, width: 96, overflow: "truncate" as const } },
      series: [{ type: "bar" as const, data: rows.map((row) => (byMoney ? row.estimated_cny : row.clicks)), itemStyle: { color: byMoney ? CHART.orange : CHART.accent, borderRadius: [0, 8, 8, 0] }, barMaxWidth: 18 }],
    };
  }, [data]);

  return (
    <>
      <label className="ops-toolbar">
        <span className="ops-muted">Days</span>
        <input className="ops-input" type="number" min={1} max={90} value={days} onChange={(e) => setDays(Number(e.target.value) || 30)} />
      </label>
      {data ? (
        <>
          <p className={data.estimated_cny === 0 && data.clicks > 0 ? "ops-callout" : "ops-callout ops-callout--ok"}>
            {data.estimated_cny === 0 && data.clicks > 0
              ? "These clicks are real. The estimate is ¥0 because the merchant rate is not set. This is not a payout."
              : "Estimated from CPA × outbound clicks. Not money collected. A booking commission needs a callback we do not have."}
          </p>
          <div className="ops-card-grid">
            <div className="ops-card"><p className="ops-card__label">Estimated CPA</p><p className="ops-card__value">¥{data.estimated_cny}</p></div>
            <div className="ops-card"><p className="ops-card__label">Outbound clicks</p><p className="ops-card__value">{data.clicks}</p></div>
            <div className="ops-card"><p className="ops-card__label">Click visitors</p><p className="ops-card__value">{data.click_uv}</p></div>
            <div className="ops-card"><p className="ops-card__label">Products clicked</p><p className="ops-card__value">{data.rows.length}</p></div>
          </div>
          <div className="ops-panel">
            <h3 className="ops-panel__title">{data.estimated_cny > 0 ? "Estimated commission" : "Clicks by product"}</h3>
            <OpsChart option={money} />
          </div>
          <div className="ops-table-wrap ops-panel">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Listing / partner</th>
                  <th>Merchant</th>
                  <th>Clicks</th>
                  <th>Rate</th>
                  <th>Est. CNY</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.key}</td>
                    <td>{row.merchant_slug || "—"}</td>
                    <td>{row.clicks}</td>
                    <td>{row.cpa_cny ? `¥${row.cpa_cny}` : "—"}</td>
                    <td>{row.estimated_cny}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </>
  );
}
